import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Star, Trophy, Crown, Gift, ArrowLeft, Gem } from 'lucide-react';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useDjaySelection, useVariantUnlocks, TOTAL_VARIANTS, useLongPressDiscovered, useDjayCustomization, useTreatmentUnlocks, TOTAL_TREATMENTS, usePersistUnlockedCosmetics, useAchievementCheckData } from '../../mascot';
import type { MascotRarity } from '../../mascot';
import { RARITY_LABELS } from '../../mascot/rarityLabels';
import { PageHero } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { SectionHeader } from '../ui/SectionHeader';
import { StatTile } from '../ui/StatTile';
import { ACHIEVEMENTS, CATEGORY_LABELS, Achievement } from '../progress/AchievementsSection';
import { CollectionLocker } from './DjayShowcase';
import { AchievementCategoryGroup } from './AchievementCategoryGroup';
import { useCollectionProgress } from './useCollectionProgress';
interface CollectionPageProps {
  state: AppState;
  selectedGroup: GroupId;
  onNavigate?: (view: string) => void;
}
/**
 * Alleen de VORMGEVING per zeldzaamheidsniveau staat hier. Het woord komt uit
 * `mascot/rarityLabels.ts` — dit bestand had een eigen labeltabel waarin niveau
 * twee "Ongewoon" heette terwijl de onthulling het "Bijzonder" noemde, en een
 * verzameling werkt alleen als de niveaus een vaste naam dragen.
 */
const RARITY_STYLES: Record<MascotRarity, {
  border: string;
  bg: string;
  text: string;
  glow: string;
  label: string;
}> = {
  common: {
    border: 'border-slate-200',
    bg: 'bg-slate-50',
    text: 'text-slate-600',
    glow: '',
    label: RARITY_LABELS.common
  },
  uncommon: {
    border: 'border-emerald-300',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    glow: 'shadow-[0_0_15px_rgba(52,211,153,0.3)]',
    label: RARITY_LABELS.uncommon
  },
  rare: {
    border: 'border-amber-300',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    glow: 'shadow-[0_0_15px_rgba(251,191,36,0.4)]',
    label: RARITY_LABELS.rare
  },
  legendary: {
    border: 'border-purple-400',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    glow: 'shadow-[0_0_20px_rgba(167,139,250,0.5)]',
    label: RARITY_LABELS.legendary
  },
  secret: {
    border: 'border-rose-400',
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    glow: 'shadow-[0_0_20px_rgba(244,63,94,0.5)]',
    label: RARITY_LABELS.secret
  }
};
export function CollectionPage({
  state,
  selectedGroup,
  onNavigate
}: CollectionPageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    markCosmeticUnlocked
  } = useAppStore();
  const {
    selectedVariant,
    setSelectedVariant,
    selectedAccessory,
    setSelectedAccessory,
    selectedTier,
    setSelectedTier,
    selectedTreatment,
    setSelectedTreatment
  } = useDjaySelection();
  // Long-press discovery now derived from the easter-egg registry
  // (audit S4, ronde 3 2026).
  const hasDiscoveredLongPress = useLongPressDiscovered();
  // Achievement-gate input assembled by a mascot-domain selector — the inline
  // totalQuestionsAnswered / masteredCount / streak-field mapping that used to
  // live here now lives in one named domain concern (useAchievementCheckData).
  const checkData = useAchievementCheckData(state, getAnsweredQuestions, getModuleMasteryForGroup);
  const [focusedVariant, setFocusedVariant] = useState<string | null>(null);
  useEffect(() => {
    const focusId = sessionStorage.getItem('collectionFocus');
    if (focusId) {
      sessionStorage.removeItem('collectionFocus');
      setFocusedVariant(focusId);
      setTimeout(() => {
        document.getElementById(`variant-${focusId}`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
      setTimeout(() => setFocusedVariant(null), 3000);
    }
  }, []);
  // --- Mascot Variants (single source of truth) ---
  const {
    variants: mascotVariants,
    unlockedCount: unlockedVariantsCount
  } = useVariantUnlocks(hasDiscoveredLongPress);
  const unlockedCosmetics = useMemo(() => state.stats.unlockedCosmetics ?? [], [state.stats.unlockedCosmetics]);
  const {
    accessories,
    tiers,
    unlockedAccessoriesCount,
    unlockedTiersCount
  } = useDjayCustomization(checkData, unlockedCosmetics);
  // --- Treatment finishes (usage-earned rarity finishes) ---
  const {
    treatments,
    unlockedCount: unlockedTreatmentsCount
  } = useTreatmentUnlocks(checkData, unlockedCosmetics);
  // Persist standalone cosmetics + treatment finishes the moment their gate is
  // first met. Domain-owned side-effect (drained out of this page body).
  usePersistUnlockedCosmetics(checkData, unlockedCosmetics, markCosmeticUnlocked);
  const achievementsWithProgress = useMemo(() => {
    return ACHIEVEMENTS.map(achievement => ({
      ...achievement,
      isUnlocked: achievement.check(checkData),
      progress: achievement.getProgress ? achievement.getProgress(checkData) : undefined
    }));
  }, [checkData]);
  // Combined collectible progress + next-reward teaser are derived in a
  // dedicated collection-domain selector (de-dups achievement-owned variants
  // vs. standalone ones, and owns the achievement tally). The two per-track
  // StatTiles below still show full per-track tallies.
  const {
    totalUnlocked,
    totalCollectibles,
    progressPercentage,
    nextReward,
    unlockedAchievementsCount,
    totalAchievementsCount
  } = useCollectionProgress(mascotVariants, achievementsWithProgress);
  const focusVariant = (variantId: string) => {
    setFocusedVariant(variantId);
    setTimeout(() => {
      document.getElementById(`variant-${variantId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 50);
    setTimeout(() => setFocusedVariant(null), 3000);
  };
  return <PageContainer className="pb-20" data-id="element-1177" data-mp="1r5bv7rn872he">
      <motion.div variants={itemVariants} className="mb-4" data-id="element-1178" data-mp="1vyn561nc3zdj">
        {onNavigate && <button onClick={() => onNavigate('progress')} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 font-bold text-sm transition-colors" data-id="element-1179" data-mp="1h98tgmwad5uz">
          
            <ArrowLeft className="w-4 h-4" data-id="element-1180" data-mp="1m68zixt4y8m3" />
            Terug naar Voortgang
          </button>}
      </motion.div>
      <motion.div variants={itemVariants} data-id="element-1181" data-mp="29w3crghd9z6v">
        <PageHero title="Mijn Collectie" subtitle="Verdien prestaties door te oefenen en speel unieke Djay-looks vrij!" gradientClasses="from-amber-500 via-orange-500 to-rose-500" djayEmotion="excited" djayVariant={selectedVariant} djayAccessory={selectedAccessory} djayTier={selectedTier} djayTreatment={selectedTreatment} icon={Sparkles} iconBgClass="bg-amber-400" floatingEmojis={['✨', '🏆', '⭐']} data-id="element-1182" data-mp="2ex92r6jn78cp" />
        
      </motion.div>

      <motion.div variants={itemVariants} className="mt-8 space-y-8" data-id="element-1183" data-mp="tkzcpzdxj8ap">
        {/* Overall Progress Card — ring-led header, then a tidy 3-up stat grid
             separated from the summary so the page reads as "one headline + its
             breakdown" instead of a crowded chip cluster. */}
        <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-sm overflow-hidden" data-id="element-1184" data-mp="vxt53awd5682">
          <div className="flex items-center gap-4 p-5 sm:p-6" data-id="element-1185" data-mp="qzrnyvihzgz8">
            <div className="relative w-16 h-16 flex-shrink-0" data-id="element-1186" data-mp="38ukm74rprzl">
              <svg className="w-16 h-16 transform -rotate-90" data-id="element-1187" data-mp="37b5ijuulmze">
                <circle cx="32" cy="32" r="28" fill="none" stroke="#F1F5F9" strokeWidth="6" data-id="element-1188" data-mp="3uhi56kv6pb63" />
                
                <circle cx="32" cy="32" r="28" fill="none" stroke="#F59E0B" strokeWidth="6" strokeDasharray="175.9" strokeDashoffset={175.9 - progressPercentage / 100 * 175.9} strokeLinecap="round" className="transition-all duration-1000 ease-out" data-id="element-1189" data-mp="d0ry5kj90w1h" />
                
              </svg>
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-1190" data-mp="3boxr49k59amc">
                <Crown className="w-6 h-6 text-amber-500" data-id="element-1191" data-mp="2q75z0pldu88u" />
              </div>
            </div>
            <div className="min-w-0" data-id="element-1192" data-mp="2v6a87g8b45z6">
              <h2 className="text-xl font-black text-slate-800 leading-tight" data-id="element-1193" data-mp="2v7bk1mx5cwhy">
                Verzameling compleet
              </h2>
              <p className="text-sm text-slate-500 font-medium" data-id="element-1194" data-mp="345ngi5kp2oib">
                {totalUnlocked} van de {totalCollectibles} behaald ·{' '}
                <span className="font-bold text-amber-600" data-id="element-1195" data-mp="394gyaa70l1oa">
                  {progressPercentage}%
                </span>
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 divide-x divide-slate-100 border-t-2 border-slate-100 bg-slate-50/60" data-id="element-1196" data-mp="3iujq472eg9l1">
            <StatTile icon={Trophy} iconColorClass="text-amber-500" label="Prestaties" value={`${unlockedAchievementsCount}/${totalAchievementsCount}`} progress={unlockedAchievementsCount / totalAchievementsCount * 100} progressColorClass="bg-amber-500" className="!p-3 sm:!p-4" data-id="element-1197" data-mp="3nsl6ulccrwtc" />
            
            <StatTile icon={Star} iconColorClass="text-orange-500" label="Varianten" value={`${unlockedVariantsCount}/${TOTAL_VARIANTS}`} progress={unlockedVariantsCount / TOTAL_VARIANTS * 100} progressColorClass="bg-orange-500" className="!p-3 sm:!p-4" data-id="element-1198" data-mp="1l8af3bp5vbqu" />
            
            <StatTile icon={Gem} iconColorClass="text-purple-500" label="Finishes" value={`${unlockedTreatmentsCount}/${TOTAL_TREATMENTS}`} progress={unlockedTreatmentsCount / TOTAL_TREATMENTS * 100} progressColorClass="bg-purple-500" className="!p-3 sm:!p-4" data-id="element-1199" data-mp="177pymm6pxs0i" />
            
          </div>
        </div>

        {/* Next reward teaser — mirrors the Voortgang GhostTrophy so the
             growth ↔ collection loop reads the same in both directions. */}
        {nextReward && <motion.button type="button" onClick={() => nextReward.variantId && focusVariant(nextReward.variantId)} whileHover={nextReward.variantId ? {
        scale: 1.01
      } : undefined} whileTap={nextReward.variantId ? {
        scale: 0.99
      } : undefined} className={`w-full flex items-center gap-4 p-4 bg-amber-50 border-2 border-amber-200 rounded-2xl text-left transition-shadow ${nextReward.variantId ? 'hover:shadow-md cursor-pointer' : 'cursor-default'}`} aria-label={nextReward.variantId ? `Volgende beloning: ${nextReward.name}. Bekijk in je collectie.` : `Volgende beloning: ${nextReward.name}.`} data-id="element-1200" data-mp="1q47wzmg2oq5q">
          
            <div className="w-12 h-12 flex-shrink-0 rounded-full bg-amber-100 text-amber-500 flex items-center justify-center" data-id="element-1201" data-mp="29gmrnnmzkfbb">
              <Gift className="w-6 h-6" data-id="element-1202" data-mp="24gs2boiuzxsd" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-1203" data-mp="2j6pqi4gsgnhk">
              <p className="text-[10px] sm:text-xs font-bold text-amber-500 uppercase tracking-wider flex items-center gap-1" data-id="element-1204" data-mp="2j5eexih3qbfw">
                <Sparkles className="w-3 h-3" data-id="element-1205" data-mp="2e9pmdz3i1ut3" /> Volgende beloning
              </p>
              <h3 className="text-sm sm:text-base font-black text-slate-800 leading-tight truncate" data-id="element-1206" data-mp="2s7xbxly7htu8">
                {nextReward.name}
              </h3>
              <div className="mt-1.5 flex items-center gap-2" data-id="element-1207" data-mp="36tl15lj9nojy">
                <div className="h-2 flex-1 bg-amber-100 rounded-full overflow-hidden" data-id="element-1208" data-mp="36s03hn1nnphv">
                  <motion.div className="h-full bg-amber-400 rounded-full" initial={{
                width: 0
              }} animate={{
                width: `${Math.round(nextReward.pct * 100)}%`
              }} transition={{
                duration: 0.6,
                ease: 'easeOut'
              }} data-id="element-1209" data-mp="3bsed39iwn7i0" />
                
                </div>
                <span className="text-[11px] font-bold text-slate-500 flex-shrink-0" data-id="element-1210" data-mp="2sutlvvso3wu8">
                  {nextReward.current}/{nextReward.max} {nextReward.label}
                </span>
              </div>
            </div>
          </motion.button>}

        {/* Interactive Djay locker (look picker) */}
        <CollectionLocker variants={mascotVariants} selectedVariant={selectedVariant} onSelectVariant={setSelectedVariant} accessories={accessories} selectedAccessory={selectedAccessory} onSelectAccessory={setSelectedAccessory} tiers={tiers} selectedTier={selectedTier} onSelectTier={setSelectedTier} treatments={treatments} selectedTreatment={selectedTreatment} onSelectTreatment={setSelectedTreatment} focusedVariant={focusedVariant} rarityStyles={RARITY_STYLES} data-id="element-1211" data-mp="2547kgnd65at7" />
        

        {/* Prestaties Section */}
        <div className="space-y-8 pt-4" data-id="element-1212" data-mp="1x8hyd8u9nhu">
          <SectionHeader title="Prestaties" icon={Trophy} accentColor="bg-amber-100 text-amber-600" data-id="element-1213" data-mp="1u474x0vbti7" />
          

          <div className="space-y-12" data-id="element-1214" data-mp="tnda4y2ubetm">
            {(Object.keys(CATEGORY_LABELS) as Achievement['category'][]).map(category => {
            const categoryAchievements = achievementsWithProgress.filter(a => a.category === category);
            if (categoryAchievements.length === 0) return null;
            return <AchievementCategoryGroup key={category} title={CATEGORY_LABELS[category]} achievements={categoryAchievements} mascotVariants={mascotVariants} data-id="element-1215" data-mp="3ac0mmleby8g4" />;
          })}
          </div>
        </div>
      </motion.div>
    </PageContainer>;
}